import React, {
  memo,
  ReactNode,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { MeasureLabelsShort, Vehicle, VehicleStatus } from '@app/models';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import CardFavoriteButton from 'components/Card/components/CardFavoriteButton/CardFavoriteButton';
import CardHideActiveButton from 'components/Card/components/CardHideActiveButton/CardHideActiveButton';
import CardThumb from 'components/Card/components/CardThumb/CardThumb';
import {
  getCompanyTypeShortName,
  getFormattedDate,
  shotName,
} from '@app/helpers';
import { IconCalendar, IconMapMarker } from '@app/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  activateVehicle,
  addToFavorites,
  deleteFromFavorites,
  hideVehicle,
  getFileById,
} from '@app/api';
import { useUserContext } from '@app/providers';

export const VehicleStatusNames: Record<VehicleStatus, string> = {
  [VehicleStatus.FREE]: 'Свободен',
  [VehicleStatus.BUSY]: 'Занят',
};

interface Props {
  vehicle: Vehicle;
  horizontal?: boolean;
  onClick?: (id: string) => void;
  fallbackImage?: string;
  onChangeFavorite?: (id: string, isFavorite: boolean) => void;
  hideThumbnail?: boolean;
}

function VehicleCard(props: Props) {
  const {
    vehicle,
    horizontal,
    onClick,
    fallbackImage,
    onChangeFavorite,
    hideThumbnail,
  } = props;
  const { owner, kato, active, isFavorite, ...vehicleItem } = vehicle;
  const location = useLocation();
  const navigate = useNavigate();
  const isProfile = location.pathname.includes('profile');
  const { user, showSignInModal } = useUserContext();

  const [avatarSrc, setAvatarSrc] = useState<string | null>(null);

  const author = !!vehicle.company
    ? {
        name: `${getCompanyTypeShortName(vehicle.company.type)} ${
          vehicle.company.name
        }`,
        image: !!vehicle.company.avatarFilesIds
          ? vehicle.company.avatarFilesIds.split(',')[0]
          : '',
      }
    : {
        name: 'Физическое лицо',
        image: !!owner?.avatarFilesIds
          ? owner.avatarFilesIds.split(',')[0]
          : '',
      };

  useEffect(() => {
    let mounted = true;
    if (author.image) {
      getFileById(author.image)
        .then((blob) => {
          if (mounted) {
            setAvatarSrc(URL.createObjectURL(blob));
          }
        })
        .catch(() => {
          if (mounted) setAvatarSrc(null);
        });
    } else {
      setAvatarSrc(null);
    }
    return () => {
      mounted = false;
    };
  }, [author.image]);

  const onClickVehicle = useCallback(() => {
    if (!!onClick) {
      navigate(`/profile/vehicles/${vehicleItem.id}`);
      return;
    }
    navigate(`/vehicles/${vehicleItem.id}`);
  }, [navigate, onClick, vehicleItem.id]);

  const onClickAddFavorite = useCallback(
    async (isFavorite: boolean) => {
      if (!user.authenticated) {
        showSignInModal();
        return;
      }

      isFavorite
        ? await addToFavorites({ vehicleId: vehicle.id })
        : await deleteFromFavorites({ vehicleId: vehicle.id });

      if (!!onChangeFavorite) {
        onChangeFavorite(vehicle.id, isFavorite);
      }
    },
    [onChangeFavorite, showSignInModal, vehicle.id, user.authenticated]
  );

  const onClickHideActive = useCallback(
    async (newActiveState: boolean) => {
      if (!user.authenticated) return;
      try {
        if (newActiveState) {
          await activateVehicle(vehicle.id);
        } else {
          await hideVehicle(vehicle.id);
        }
      } catch (e) {
        console.error('Failed to update vehicle status', e);
        throw e;
      }
    },
    [vehicle.id, user.authenticated]
  );

  const actionButtons = useMemo<ReactNode[]>(() => {
    if (!vehicle) return [];

    const returnActionButtons = [
      <Button
        key="details"
        onClick={onClickVehicle}
        className="flex-1 text-xs sm:text-sm px-2 py-1 sm:px-3 sm:py-2"
        variant="default"
        size="sm"
      >
        Подробнее
      </Button>,
      <CardFavoriteButton
        key="favorite"
        onClickFavorite={onClickAddFavorite}
        isFavorite={isFavorite}
      />,
    ];

    if (isProfile && vehicle.ownerId == user.id) {
      returnActionButtons.push(
        <CardHideActiveButton
          key="hideactive"
          onClickHideActive={onClickHideActive}
          isActive={active}
        />
      );
    }

    return [...returnActionButtons];
  }, [
    active,
    isFavorite,
    isProfile,
    onClickAddFavorite,
    onClickHideActive,
    vehicle,
    user.id,
    onClickVehicle,
  ]);

  const imageIds = useMemo(() => {
    return vehicle.imagesIds ? vehicle.imagesIds.split(',') : [];
  }, [vehicle.imagesIds]);

  const infos = useMemo(
    () => [
      { icon: IconMapMarker, text: kato.nameRu },
      {
        icon: IconCalendar,
        text: `Дата публикации: ${getFormattedDate(vehicleItem.createdAt)}`,
      },
    ],
    [kato.nameRu, vehicleItem.createdAt]
  );

  return (
    <Card
      className={`bg-white border rounded-xl shadow-sm hover:shadow-lg hover:-translate-y-1 hover:bg-blue-50 transition-all duration-200 w-full overflow-hidden ${
        horizontal ? 'flex flex-col sm:flex-row items-stretch' : 'flex flex-col'
      } cursor-pointer`}
      onClick={onClickVehicle}
    >
      {!hideThumbnail && (
        <div
          className={`relative flex-shrink-0 hidden sm:block ${
            horizontal
              ? 'w-full h-40 sm:w-32 md:w-40 lg:w-48 sm:h-full min-h-[160px]'
              : 'w-full h-48 sm:h-56 md:h-64'
          }`}
        >
          <CardThumb images={imageIds} placeholder={fallbackImage} />
        </div>
      )}

      <div
        className={`flex-1 flex flex-col min-w-0 ${
          horizontal ? 'sm:flex-row' : ''
        }`}
      >
        <div className="flex-1 px-3 sm:px-4 py-3 min-w-0">
          <CardTitle className="text-sm sm:text-base font-semibold line-clamp-2 mb-2 break-words">
            {vehicleItem.type}
          </CardTitle>

          <div className="space-y-1 text-xs text-gray-500 mb-2">
            {infos.map((info, index) => (
              <div key={index} className="flex items-start gap-1">
                <info.icon className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0 mt-0.5" />
                <span className="break-words leading-tight">{info.text}</span>
              </div>
            ))}
          </div>

          <CardDescription className="text-xs sm:text-sm text-gray-700 line-clamp-2 mb-3 break-words">
            {vehicleItem.description}
          </CardDescription>

          <div
            className={`flex items-center gap-2 ${
              horizontal ? 'sm:hidden' : ''
            }`}
          >
            {author.image && (
              <Avatar className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0">
                <AvatarImage
                  src={avatarSrc || undefined}
                  alt={author.name}
                  className="object-cover"
                />
                <AvatarFallback className="text-xs">
                  {author.name.charAt(0)}
                </AvatarFallback>
              </Avatar>
            )}
            <div className="text-xs sm:text-sm min-w-0">
              <div className="font-medium truncate">{author.name}</div>
              <div className="text-gray-500 truncate">
                {owner ? shotName(owner) : ''}
              </div>
            </div>
          </div>
        </div>

        <div
          className={`flex flex-col justify-between px-3 sm:px-4 pb-3 ${
            horizontal ? 'sm:min-w-[160px] sm:max-w-[200px] sm:py-3' : ''
          }`}
        >
          {horizontal && (
            <div className="hidden sm:flex items-center gap-2 mb-3">
              {author.image && (
                <Avatar className="w-8 h-8 flex-shrink-0">
                  <AvatarImage
                    src={avatarSrc || undefined}
                    alt={author.name}
                    className="object-cover"
                  />
                  <AvatarFallback className="text-xs">
                    {author.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
              )}
              <div className="text-xs min-w-0">
                <div className="font-medium truncate">{author.name}</div>
                <div className="text-gray-500 truncate">
                  {owner ? shotName(owner) : ''}
                </div>
              </div>
            </div>
          )}

          <div className="text-right mb-3">
            <Badge className="bg-green-600 text-white px-2 py-1 rounded text-xs mb-2">
              {VehicleStatusNames[vehicleItem.status as VehicleStatus]}
            </Badge>
            <div className="text-lg sm:text-xl font-bold text-blue-700">
              <span className="break-all">{vehicleItem.price}₸</span>
              {vehicleItem.measure && (
                <div className="text-xs sm:text-sm font-normal text-gray-500">
                  / {MeasureLabelsShort[vehicleItem.measure]}
                </div>
              )}
            </div>
          </div>

          <div
            className={`flex gap-1 sm:gap-2 ${
              horizontal ? 'flex-col sm:flex-col' : 'flex-row flex-wrap'
            }`}
          >
            {actionButtons}
          </div>
        </div>
      </div>
    </Card>
  );
}

export default memo(VehicleCard);
